import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { MapPin, Phone, Globe, ShieldCheck, Sprout } from "lucide-react";
import Seo from "@/components/Seo";
import OnlineBadge from "@/components/OnlineBadge";
import { resolveImageUrl } from "@/components/ImageUploader";

export default function HobbyFarmers() {
  const [farmers, setFarmers] = useState([]);
  const [states, setStates] = useState([]);
  const [stateFilter, setStateFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/states").then((r) => setStates(r.data));
  }, []);

  useEffect(() => {
    const params = {};
    if (stateFilter !== "all") params.state = stateFilter;
    if (search) params.search = search;
    api.get("/hobby-farmers", { params }).then((r) => setFarmers(r.data));
  }, [stateFilter, search]);

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16">
      <Seo
        url="/hobby-farmers"
        title="Hobby Farmer Directory"
        description="Browse verified hobby farmers across every state. Each profile lists what they grow, where they're based, and how to reach them directly."
      />
      <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Directory</div>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-stone-900">Meet the hobby farmers.</h1>
      <p className="mt-4 font-sans text-stone-600 max-w-2xl">
        Browse verified hobby farmers across every state. Each profile lists what they grow, what they sell, where they're based, and how to reach them directly.
      </p>

      <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-2xl">
        <Input
          data-testid="farmer-search"
          placeholder="Search by farm name, city, or service..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-white border-stone-200"
        />
        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger data-testid="farmer-state-filter" className="bg-white border-stone-200">
            <SelectValue placeholder="All States" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All States</SelectItem>
            {states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {farmers.length === 0 ? (
        <div className="mt-12 bg-white border border-stone-200 rounded-md p-12 text-center">
          <Sprout className="mx-auto text-stone-400" size={32} />
          <div className="mt-4 font-serif text-2xl text-stone-700">No hobby farmer profiles yet.</div>
          <p className="mt-2 text-stone-500 font-sans text-sm">As soon as our verified hobby farmers complete their profiles, they'll show up here.</p>
        </div>
      ) : (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {farmers.map((f) => (
            <Link
              key={f.user_id}
              to={`/hobby-farmers/${f.user_id}`}
              data-testid={`farmer-card-${f.user_id}`}
              className="bg-white border border-stone-200 rounded-md overflow-hidden lift block"
            >
              {f.profile_image_url && (
                <div className="aspect-[16/10] overflow-hidden bg-stone-100">
                  <img src={resolveImageUrl(f.profile_image_url)} alt={f.farm_name} loading="lazy" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-serif text-2xl text-stone-900 leading-tight">{f.farm_name}</div>
                    <div className="text-xs text-stone-500 mt-1 font-sans">{f.name}</div>
                  </div>
                  {f.verified && (
                    <Badge className="bg-emerald-800 text-stone-50 text-[10px] uppercase tracking-wider"><ShieldCheck size={10} className="mr-1" />Verified</Badge>
                  )}
                </div>
                <div className="mt-2"><OnlineBadge isOnline={f.is_online} /></div>
                {f.services && (
                  <p className="mt-4 text-sm text-stone-600 font-sans line-clamp-2">{f.services}</p>
                )}
                <div className="mt-5 flex flex-col gap-2 text-xs text-stone-600 font-sans">
                  {(f.city || f.state || f.country) && (
                    <span className="flex items-center gap-2"><MapPin size={12} />{[f.city, f.state, f.country].filter(Boolean).join(", ")}</span>
                  )}
                  {f.phone && <span className="flex items-center gap-2"><Phone size={12} />{f.phone}</span>}
                  {f.website && <span className="flex items-center gap-2 truncate"><Globe size={12} />{f.website}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
